import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subject, Subscription, from, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ThemingService } from '../../../services/theming.service';
import {
  AddressAutocompleteService,
  AddressSuggestion,
  applyUnitToSuggestions,
  mergeUnitIntoAddress,
} from '../../../services/address-autocomplete.service';

@Component({
  selector: 'tandem-form-address-autocomplete',
  templateUrl: './form-address-autocomplete.component.html',
  styleUrls: ['./form-address-autocomplete.component.scss'],
})
export class FormAddressAutocompleteComponent implements OnInit, OnDestroy {
  @Input() label = '';
  @Input() form: FormGroup | AbstractControl = new FormGroup({});
  @Input() controlName = '';
  @Input() inputId = '';
  @Input() placeholder = '';
  @Input() enableZillow = true;
  @Input() isDisabled = false;

  control: FormControl = new FormControl();
  suggestions: AddressSuggestion[] = [];
  showDropdown = false;
  activeIndex = -1;
  validPlaceSelected = false;
  loading = false;

  private inputSubject = new Subject<string>();
  private sub = new Subscription();
  private lastUserInput = '';
  /** Places text before the typed unit is applied. Labels are derived from this. */
  private rawSuggestions: AddressSuggestion[] = [];

  constructor(
    private addressAutocompleteService: AddressAutocompleteService,
    private elRef: ElementRef,
    private themingService: ThemingService,
    private cdr: ChangeDetectorRef,
  ) {}

  get ringStyle() {
    const color = this.themingService.selectedTheme.value;
    return { 'border-color': `${color} transparent transparent transparent` };
  }

  ngOnInit(): void {
    this.control = this.form.get(this.controlName) as FormControl;

    this.sub = this.inputSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.length < 3) {
          this.loading = false;
          return of([] as AddressSuggestion[]);
        }
        this.loading = true;
        return from(this.addressAutocompleteService.search(query)).pipe(
          catchError(() => of([] as AddressSuggestion[]))
        );
      })
    ).subscribe(results => {
      this.loading = false;
      this.rawSuggestions = results;
      this.syncSuggestionLabels();
      this.showDropdown = this.suggestions.length > 0;
      this.activeIndex = -1;
      // The Places script/API call resolves outside Angular's zone-tracked event sources.
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.addressAutocompleteService.endSession();
  }

  onInput(event: Event): void {
    this.validPlaceSelected = false;
    const value = (event.target as HTMLInputElement).value;
    this.lastUserInput = value;
    this.syncSuggestionLabels();
    this.inputSubject.next(value);
  }

  selectSuggestion(suggestion: AddressSuggestion): void {
    const source =
      this.rawSuggestions.find((item) => item.placeId === suggestion.placeId) ?? suggestion;
    const finalAddress = mergeUnitIntoAddress(source.label, this.lastUserInput);
    this.control.setValue(finalAddress);
    this.rawSuggestions = [];
    this.suggestions = [];
    this.showDropdown = false;
    this.validPlaceSelected = true;
    this.activeIndex = -1;
    this.addressAutocompleteService.endSession();
  }

  /** Rebuild dropdown labels from the Places text plus the unit the user typed. */
  private syncSuggestionLabels(): void {
    this.suggestions = applyUnitToSuggestions(this.rawSuggestions, this.lastUserInput);
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.showDropdown) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, -1);
        break;
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.selectSuggestion(this.suggestions[this.activeIndex]);
        }
        break;
      case 'Escape':
        this.showDropdown = false;
        this.activeIndex = -1;
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  get zillowUrl(): string {
    const raw = this.control?.value;
    if (!raw || !this.enableZillow || !this.validPlaceSelected) return '';
    return `https://www.zillow.com/homes/${encodeURIComponent(String(raw).trim())}/`;
  }
}
